import React, { useState, useRef, useEffect } from "react";
import { FileAttachment } from "./attachment";
import { ArrowLeftCircle, ArrowRightCircle } from "lucide-react";

interface SelectedFile {
    file: File;
    id: string;
    showRemove: boolean;
    showDownload?: boolean;
}

/**
 * Props for the FileAttachmentWrapper component
 */
interface FileAttachmentWrapperProps {
    /** List of selected files to display */
    fileList?: SelectedFile[];
    /** List of uploaded files to display */
    uploadList?: any;
    /** Whether this is used in an input context. Defaults to false. */
    isInput?: boolean;
    /** Callback function called when a file is removed */
    handleRemoveFile?: (id: string) => void;
}

/**
 * FileAttachmentWrapper component displays a horizontally scrollable list of file attachments.
 * Automatically shows left/right scroll arrows when content overflows. Supports both
 * local files and uploaded files. Handles scroll detection and arrow visibility.
 * 
 * @component
 * @param {FileAttachmentWrapperProps} props - Component props
 * @param {SelectedFile[]} [props.fileList] - List of selected files
 * @param {any[]} [props.uploadList] - List of uploaded files
 * @param {boolean} [props.isInput=false] - Whether used in input context
 * @param {(id: string) => void} [props.handleRemoveFile] - Callback fired when file removed
 * @returns {JSX.Element} Scrollable container with file attachments and scroll arrows
 * 
 * @example
 * ```tsx
 * <FileAttachmentWrapper 
 *   fileList={files}
 *   handleRemoveFile={handleRemove}
 *   isInput={true}
 * />
 * ```
 */
export const FileAttachmentWrapper: React.FC<FileAttachmentWrapperProps> = ({ fileList, uploadList, handleRemoveFile, isInput = false }) => {

    const containerRef = useRef<HTMLDivElement | null>(null);
    const [showLeft, setShowLeft] = useState(false);
    const [showRight, setShowRight] = useState(false);

    const checkScroll = () => {
        const el = containerRef.current;
        if (!el) return;

        const atLeft = el.scrollLeft === 0;
        const atRight = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;

        setShowLeft(!atLeft);
        setShowRight(!atRight && el.scrollWidth > el.clientWidth);
    };

    const scrollLeftAction = () => {
        containerRef.current?.scrollBy({ left: -300, behavior: "smooth" });
    };

    const scrollRightAction = () => {
        containerRef.current?.scrollBy({ left: 300, behavior: "smooth" });
    };

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        checkScroll();

        el.addEventListener("scroll", checkScroll);

        const resizeObserver = new ResizeObserver(() => {
            checkScroll();
        });
        resizeObserver.observe(el);

        return () => {
            el.removeEventListener("scroll", checkScroll);
            resizeObserver.disconnect();
        };
    }, [fileList?.length, uploadList]);

    return <div ref={containerRef} className="overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
        <div className={`flex gap-2 min-w-max pb-1 ${isInput ? '' : 'justify-end'}`}>
            {fileList?.map((file) => (
                <FileAttachment
                    key={file.id}
                    file={file.file}
                    onRemove={() => handleRemoveFile?.(file.id)}
                    showRemove={file.showRemove}
                />
            ))}
            {uploadList?.map((file: any) => (
                <FileAttachment
                    key={file.id}
                    upload={file.upload}
                    showRemove={file.showRemove}
                    showDownload={file.showDownload}
                />
            ))}
        </div>
        {showLeft && (
            <button
                onClick={scrollLeftAction}
                className={`absolute left-0 top-0 bottom-0 flex items-center bg-gradient-to-r from-50% ${isInput ? 'px-5 pl-3 from-white' : 'from-[#f6fbfd]'}`}
            >
                <ArrowLeftCircle className={`text-gray-500 bg-white rounded-full`} />
            </button>
        )}
        {showRight && (
            <button
                onClick={scrollRightAction}
                className={`absolute right-0 top-0 bottom-0 flex items-center bg-gradient-to-l from-50% ${isInput ? 'px-5 pr-3 from-white' : 'from-[#f6fbfd]'}`}
            >
                <ArrowRightCircle className={`text-gray-500 bg-white rounded-full`} />
            </button>
        )}
    </div>;
}